import { Link, useLocation } from "react-router";
import { z } from "zod";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePrices, useRoster } from "@/lib/hooks";
import { formatCurrency } from "@/lib/formatters";
import { REPLACEMENT_PENDING_LABEL, STATUS_LABELS, availableAction } from "@/lib/roster";
import type { CreateOrderInput, Employee } from "@/lib/api";
import { usePaymentSubmission } from "@/lib/payment-flow";
import {
  addCardIssues,
  buildPaymentFields,
  paymentFormShape,
  scrollToFirstError,
  useRosterCheckoutSuccess,
} from "@/lib/checkout-form";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import { OrderSummaryLines } from "@/components/OrderSummary";
import {
  CheckoutPaymentSection,
  CheckoutSubmitSection,
} from "@/components/RosterCheckoutParts";

const replacementSchema = z
  .object({
    employeeIds: z.array(z.string()).min(1, "Select at least one employee"),
    ...paymentFormShape,
  })
  .superRefine((data, ctx) => addCardIssues(data.card, ctx));

type ReplacementFormData = z.infer<typeof replacementSchema>;

function buildReplacementOrder(data: ReplacementFormData, idempotencyKey: string): CreateOrderInput {
  return {
    type: "badge_replacement",
    employeeIds: data.employeeIds,
    ...buildPaymentFields(data, idempotencyKey),
  };
}

/** The server's rule, from the one place the client states it: an active
 *  employee whose badge is made or delivered, with no replacement on order. */
function canReplace(employee: Employee): boolean {
  return availableAction(employee).kind === "replacement";
}

/** Ids the roster page handed over in router state, kept only if they are strings. */
function preselectedIds(state: unknown): string[] {
  const ids = (state as { employeeIds?: unknown } | null)?.employeeIds;
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
}

function ReplacementForm({
  employees,
  preselected,
}: {
  employees: Employee[];
  preselected: string[];
}): React.ReactElement {
  const { data: prices, isError: priceFailed } = usePrices();
  const onSuccess = useRosterCheckoutSuccess();

  const eligible = employees.filter(canReplace);
  const eligibleIds = new Set(eligible.map((e) => e.id));

  const methods = useForm<ReplacementFormData>({
    resolver: zodResolver(replacementSchema),
    defaultValues: {
      // An id the roster page sent that is not (or no longer) eligible is dropped.
      employeeIds: preselected.filter((id) => eligibleIds.has(id)),
      billingAddress: { street: "", city: "", state: "", zip: "" },
      paymentMethod: "credit_card",
      card: { cardNumber: "", expireMonth: "", expireYear: "", cvv: "", nameOnCard: "" },
    },
  });

  // Literal flow: this page's own key slot, never the public checkout's.
  const payment = usePaymentSubmission<ReplacementFormData>({
    flow: "replacement",
    buildOrder: buildReplacementOrder,
    onSuccess,
  });

  const selected = useWatch({ control: methods.control, name: "employeeIds" }) ?? [];
  const count = selected.length;
  const total = prices === undefined ? undefined : count * prices.badgePrice;

  function toggle(id: string, checked: boolean): void {
    const next = checked ? [...selected, id] : selected.filter((existing) => existing !== id);
    methods.setValue("employeeIds", next, { shouldValidate: methods.formState.isSubmitted });
  }

  const idsError = methods.formState.errors.employeeIds?.message;
  // Eligible rows first; the rest stay visible so the vendor can see why not.
  const ordered = [...eligible, ...employees.filter((e) => !canReplace(e))];

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => void methods.handleSubmit(payment.submit, scrollToFirstError)(e)}
        noValidate
        className="space-y-8"
      >
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Who needs a replacement?</h2>
          <ul className="space-y-2">
            {ordered.map((employee) => {
              const ok = canReplace(employee);
              const name = `${employee.firstName} ${employee.lastName}`;
              const reason = employee.inactive
                ? "No longer employed"
                : availableAction(employee).kind === "replacement_pending"
                  ? REPLACEMENT_PENDING_LABEL
                  : STATUS_LABELS[employee.status];
              return (
                <li key={employee.id}>
                  <label
                    className={`flex items-start gap-3 rounded-md border border-gray-200 bg-white p-3 text-sm ${
                      ok ? "cursor-pointer" : "opacity-60"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selected.includes(employee.id)}
                      disabled={!ok}
                      onChange={(e) => toggle(employee.id, e.target.checked)}
                    />
                    <span>
                      <span className="font-medium text-gray-900">{name}</span>
                      <span className="block text-gray-600">{reason}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          {idsError && <p className="text-xs text-red-600">{idsError}</p>}
        </section>

        <CheckoutPaymentSection />

        <section className="rounded-md border border-gray-200 bg-white p-4">
          <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>
          {prices === undefined ? (
            <p className="mt-2 text-sm text-gray-500">
              {priceFailed ? "Price unavailable." : "Loading pricing..."}
            </p>
          ) : count === 0 ? (
            <p className="mt-2 text-sm text-gray-500">Select an employee to see the total.</p>
          ) : (
            <OrderSummaryLines
              lines={[
                {
                  label: count === 1 ? "replacement badge" : "replacement badges",
                  quantity: count,
                  unitPrice: prices.badgePrice,
                },
              ]}
            />
          )}
        </section>

        <CheckoutSubmitSection
          payment={payment}
          priceFailed={priceFailed}
          label={
            total === undefined
              ? undefined
              : count === 0
                ? "Place Order" // submitting with nothing selected shows the inline error
                : `Place Order — ${formatCurrency(total)}`
          }
        />
      </form>
    </FormProvider>
  );
}

export function ReplacementCheckoutPage(): React.ReactElement {
  const location = useLocation();
  const roster = useRoster();

  // The form mounts only once the roster is known: its default selection depends
  // on who is eligible, and the hook should not mint a key for a page that cannot
  // place an order.
  if (roster.isLoading) return <LoadingSpinner />;
  if (roster.error) {
    return (
      <ErrorMessage
        message={roster.error instanceof Error ? roster.error.message : "Failed to load your employees"}
      />
    );
  }

  const employees = roster.data?.employees ?? [];
  const canAny = employees.some(canReplace);
  // Badge holders exist, but every one already has a replacement on order.
  const allPending =
    !canAny && employees.some((e) => availableAction(e).kind === "replacement_pending");

  return (
    <div className="mx-auto max-w-[640px]">
      <h1 className="mb-1 text-2xl font-bold text-gray-900">Order replacement badges</h1>
      <p className="mb-6 text-sm text-gray-600">
        For employees whose badge has been made or delivered. A replacement does not change
        their background check or badge expiry.
      </p>
      {canAny ? (
        <ReplacementForm employees={employees} preselected={preselectedIds(location.state)} />
      ) : (
        <div className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-700">
          <p>
            {allPending
              ? "Everyone with a badge already has a replacement on order."
              : "None of your employees has a badge to replace yet."}
          </p>
          <Link to="/roster" className="mt-2 inline-block font-medium text-blue-700 hover:text-blue-900">
            Back to My Employees
          </Link>
        </div>
      )}
    </div>
  );
}
